import { Track } from "@/src/lib/tracking";
import {
  WebSocketConfig,
  ConnectionState,
  SubscriptionCallback,
  SubscribeOptions,
  SendOptions,
  WebSocketEventType,
  WebSocketEventListener,
  ReconnectConfig,
} from "./types";
import { JSONMessageParser } from "./parsers";
import { HeartbeatManager } from "./HeartbeatManager";
import { SubscriptionManager } from "./SubscriptionManager";
import { RequestResponseManager } from "./RequestResponseManager";

/**
 * WebSocket 客户端
 * 提供完整的 WebSocket 连接管理功能
 * @template T 消息数据类型,默认为 any
 */
export class WebSocketClient<T = any> {
  private ws: WebSocket | null = null;
  private config: Required<Omit<WebSocketConfig, "protocols">> &
    Pick<WebSocketConfig, "protocols">;
  private reconnectConfig: Required<ReconnectConfig>;
  private state: ConnectionState = ConnectionState.DISCONNECTED;
  private reconnectAttempts: number = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private connectionTimer: NodeJS.Timeout | null = null;
  private messageQueue: Array<{ message: string; options: SendOptions }> = [];
  private isManualDisconnect: boolean = false; // ✅ 添加标志：是否是主动断开

  private heartbeatManager: HeartbeatManager;
  private subscriptionManager: SubscriptionManager;
  private requestResponseManager: RequestResponseManager;
  private eventListeners: Map<
    WebSocketEventType,
    Set<WebSocketEventListener<T>>
  > = new Map();

  constructor(config: WebSocketConfig) {
    // 设置默认配置
    this.config = {
      url: config.url,
      parser: config.parser || new JSONMessageParser(),
      heartbeat: {
        enabled: false,
        interval: 30000,
        timeout: 10000,
        message: "ping",
        expectResponse: false,
        responseValidator: () => true,
        ...config.heartbeat,
      },
      reconnect: {
        enabled: true,
        maxAttempts: 5,
        delay: 1000,
        maxDelay: 30000,
        exponentialBackoff: true,
        ...config.reconnect,
      },
      debug: config.debug ?? false,
      protocols: config.protocols,
      connectionTimeout: config.connectionTimeout ?? 10000,
    };

    this.reconnectConfig = this.config.reconnect as Required<ReconnectConfig>;

    // 初始化管理器
    this.heartbeatManager = new HeartbeatManager(this.config.heartbeat);
    this.subscriptionManager = new SubscriptionManager(this.config.debug);
    this.requestResponseManager = new RequestResponseManager(this.config.debug);

    // 设置订阅管理器的发送回调
    this.subscriptionManager.setSendCallback((topic: string) => {
      this.send(topic);
    });
  }

  /**
   * 连接 WebSocket
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      if (this.config.debug) {
      }
      return;
    }

    if (this.state === ConnectionState.CONNECTING) {
      if (this.config.debug) {
      }
      return;
    }

    // ✅ 重置主动断开标志
    this.isManualDisconnect = false;

    this.setState(ConnectionState.CONNECTING);

    try {
      this.ws = new WebSocket(this.config.url, this.config.protocols);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);

      // 设置连接超时
      if (this.config.connectionTimeout > 0) {
        this.connectionTimer = setTimeout(() => {
          if (this.state === ConnectionState.CONNECTING) {
            Track.log("error", "WebSocketClient", "连接超时");
            this.ws?.close();
            this.handleConnectionTimeout();
          }
        }, this.config.connectionTimeout);
      }
    } catch (error) {
      Track.error(error, {
        tag: "WebSocketClient",
        msg: "WebSocket 连接失败:",
      });
      this.setState(ConnectionState.ERROR);
      this.reconnect();
    }
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    if (this.config.debug) {
    }

    // ✅ 标记为主动断开，防止触发自动重连
    this.isManualDisconnect = true;

    this.setState(ConnectionState.DISCONNECTING);

    // 停止心跳
    this.heartbeatManager.stop();

    // 清除重连定时器
    this.clearReconnectTimer();

    // 清除连接超时定时器
    this.clearConnectionTimer();

    // 清除所有待处理的请求
    this.requestResponseManager.clear();

    // 关闭 WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.setState(ConnectionState.DISCONNECTED);
  }

  /**
   * 订阅主题
   */
  subscribe(
    topic: string,
    callback: SubscriptionCallback<T>,
    options?: SubscribeOptions,
  ): () => void {
    return this.subscriptionManager.subscribe(topic, callback, options);
  }

  /**
   * 取消订阅
   */
  unsubscribe(topic: string, callback?: SubscriptionCallback<T>): void {
    this.subscriptionManager.unsubscribe(topic, callback);
  }

  /**
   * 取消所有订阅
   */
  unsubscribeAll(): void {
    this.subscriptionManager.unsubscribeAll();
  }

  /**
   * 发送消息
   */
  send(message: string | object, options: SendOptions = {}): string | void {
    const { queue = true, expectResponse = false } = options;

    // 生成请求 ID（如果需要响应）
    let requestId: string | undefined;
    let messageToSend: string | object = message;

    if (expectResponse) {
      requestId = this.requestResponseManager.generateRequestId();

      // 如果消息是对象，注入请求 ID
      if (typeof message === "object") {
        messageToSend = {
          ...message,
          requestId,
        };
      }
      // 如果消息是字符串，尝试解析并注入请求 ID
      else {
        try {
          const parsed = JSON.parse(message);
          messageToSend = {
            ...parsed,
            requestId,
          };
        } catch {
          // 如果无法解析，保持原样
          if (this.config.debug) {
            Track.log(
              "warn",
              "WebSocketClient",
              "无法为字符串消息注入请求 ID，消息将保持原样",
            );
          }
        }
      }
    }

    // 序列化消息
    const serialized =
      typeof messageToSend === "string"
        ? messageToSend
        : this.config.parser.serialize(messageToSend);

    // 如果连接未就绪且允许排队
    if (this.ws?.readyState !== WebSocket.OPEN) {
      if (queue) {
        this.messageQueue.push({ message: serialized, options });
        if (this.config.debug) {
        }
        return requestId;
      } else {
        const error = new Error("WebSocket 未连接");
        Track.error(error, { tag: "WebSocketClient", msg: "发送消息失败:" });
        if (options.onError) {
          options.onError(error);
        }
        return requestId;
      }
    }

    // 注册请求（如果需要响应）
    if (expectResponse && requestId) {
      this.requestResponseManager.registerRequest(serialized, {
        ...options,
        expectResponse: true,
      });
    }

    // 发送消息
    try {
      this.ws.send(serialized);

      if (this.config.debug) {
      }

      if (options.onSuccess) {
        options.onSuccess();
      }

      // 发送消息表示连接活跃,重置心跳
      this.heartbeatManager.reset();

      return requestId;
    } catch (error) {
      Track.error(error, { tag: "WebSocketClient", msg: "发送消息失败:" });

      // 如果注册了请求，需要移除
      if (requestId) {
        this.requestResponseManager.removeRequest(requestId);
      }

      if (options.onError) {
        options.onError(error as Error);
      }

      return requestId;
    }
  }

  /**
   * 添加事件监听器
   */
  on(
    event: WebSocketEventType,
    listener: WebSocketEventListener<T>,
  ): () => void {
    let listeners = this.eventListeners.get(event);
    if (!listeners) {
      listeners = new Set();
      this.eventListeners.set(event, listeners);
    }
    listeners.add(listener);

    // 返回移除监听器的函数
    return () => {
      this.off(event, listener);
    };
  }

  /**
   * 移除事件监听器
   */
  off(event: WebSocketEventType, listener: WebSocketEventListener<T>): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  /**
   * 触发事件
   */
  private emit(event: WebSocketEventType, data?: T | any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(data);
        } catch (error) {
          Track.log(
            "error",
            "WebSocketClient",
            `事件监听器执行出错 (${event}):`,
            { cause: String(error) },
          );
        }
      });
    }
  }

  /**
   * 获取当前连接状态
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return (
      this.state === ConnectionState.CONNECTED &&
      this.ws?.readyState === WebSocket.OPEN
    );
  }

  /**
   * 获取配置
   */
  getConfig(): Required<Omit<WebSocketConfig, "protocols">> &
    Pick<WebSocketConfig, "protocols"> {
    return { ...this.config };
  }

  /**
   * 更新配置
   */
  updateConfig(config: Partial<WebSocketConfig>): void {
    const needsReconnect = config.url && config.url !== this.config.url;

    this.config = {
      ...this.config,
      ...config,
    };

    // 更新心跳配置
    if (config.heartbeat) {
      this.heartbeatManager.updateConfig(config.heartbeat);
    }

    // 如果 URL 改变,需要重新连接
    if (needsReconnect && this.isConnected()) {
      this.disconnect();
      this.connect();
    }
  }

  /**
   * 获取订阅信息
   */
  getSubscriptionInfo() {
    return this.subscriptionManager.getSubscriptionInfo();
  }

  /**
   * 获取待处理请求信息
   */
  getPendingRequestsInfo() {
    return {
      count: this.requestResponseManager.getPendingCount(),
      requests: this.requestResponseManager.getPendingRequests(),
    };
  }

  /**
   * 处理连接打开
   */
  private handleOpen(event: Event): void {
    if (this.config.debug) {
    }

    this.clearConnectionTimer();
    this.setState(ConnectionState.CONNECTED);
    this.reconnectAttempts = 0;

    // 触发打开事件
    this.emit(WebSocketEventType.OPEN, event);

    // 启动心跳
    this.heartbeatManager.start(
      (message: string) => {
        this.send(message, { queue: false });
      },
      () => {
        Track.log("warn", "WebSocketClient", "心跳超时,触发重连");
        this.reconnect();
      },
    );

    // 重新订阅所有主题
    this.subscriptionManager.resubscribeAll();

    // 处理消息队列
    this.flushMessageQueue();
  }

  /**
   * 处理连接关闭
   */
  private handleClose(event: CloseEvent): void {
    if (this.config.debug) {
    }

    this.clearConnectionTimer();
    this.heartbeatManager.stop();
    this.setState(ConnectionState.DISCONNECTED);

    // 触发关闭事件
    this.emit(WebSocketEventType.CLOSE, event);

    // 标记所有订阅为未发送状态
    this.subscriptionManager.resetSentStatus();

    // 重置待处理的请求（触发超时错误）
    this.requestResponseManager.reset(true);

    // ✅ 只有在非主动断开且启用了重连的情况下才尝试重连
    if (!this.isManualDisconnect && this.reconnectConfig.enabled) {
      if (this.config.debug) {
      }
      this.reconnect();
    } else if (this.isManualDisconnect && this.config.debug) {
    }
  }

  /**
   * 处理错误
   */
  private handleError(error: Event): void {
    Track.error(error, { tag: "WebSocketClient", msg: "WebSocket 错误:" });
    this.setState(ConnectionState.ERROR);

    // 触发错误事件
    this.emit(WebSocketEventType.ERROR, error);
  }

  /**
   * 处理消息
   */
  private handleMessage(event: MessageEvent): void {
    try {
      // Fix: Use indexOf to split only on the first '#' occurrence to prevent breaking JSON content containing '#'
      const dataStr = String(event.data);
      const firstHashIndex = dataStr.indexOf("#");
      const msgFlag =
        firstHashIndex !== -1 ? dataStr.substring(0, firstHashIndex) : dataStr;
      const msgData =
        firstHashIndex !== -1
          ? dataStr.substring(firstHashIndex + 1)
          : undefined;

      switch (msgFlag) {
        case "RO":
          break;
        case "NF":
          if (msgData === undefined) {
            if (this.config.debug) {
              Track.log(
                "warn",
                "WebSocketClient",
                "Received NF message without data",
              );
            }
            return;
          }

          // 解析消息
          const parsedData = this.config.parser.parse(msgData);

          if (parsedData === null) {
            if (this.config.debug) {
            }
            return;
          }

          if (this.config.debug) {
          }

          // 检查是否是心跳响应
          const isHeartbeatResponse =
            this.heartbeatManager.handleResponse(parsedData);

          if (!isHeartbeatResponse) {
            // 尝试匹配请求-响应
            const isRequestResponse =
              this.requestResponseManager.matchResponse(parsedData);

            // 如果不是请求响应，则进行常规处理
            if (!isRequestResponse) {
              // 触发消息事件
              this.emit(WebSocketEventType.MESSAGE, parsedData);

              // 广播到所有订阅
              this.subscriptionManager.broadcast(parsedData);
            }
          }

          // 重置心跳(收到任何消息都表示连接活跃)
          this.heartbeatManager.reset();
          break;
        default:
          break;
      }
    } catch (error) {
      Track.error(error, { tag: "WebSocketClient", msg: "消息处理错误:" });
    }
  }

  /**
   * 处理连接超时
   */
  private handleConnectionTimeout(): void {
    Track.log("error", "WebSocketClient", "连接超时");
    this.setState(ConnectionState.ERROR);
    this.reconnect();
  }

  /**
   * 重连
   */
  private reconnect(): void {
    if (!this.reconnectConfig.enabled) {
      if (this.config.debug) {
      }
      return;
    }

    if (this.reconnectAttempts >= this.reconnectConfig.maxAttempts) {
      Track.log("error", "WebSocketClient", "达到最大重连次数");
      this.emit(WebSocketEventType.RECONNECT_FAILED, {
        attempts: this.reconnectAttempts,
      });
      return;
    }

    this.clearReconnectTimer();

    this.reconnectAttempts++;
    this.setState(ConnectionState.RECONNECTING);

    // 计算重连延迟
    let delay = this.reconnectConfig.delay;
    if (this.reconnectConfig.exponentialBackoff) {
      delay = Math.min(
        delay * Math.pow(2, this.reconnectAttempts - 1),
        this.reconnectConfig.maxDelay,
      );
    }

    if (this.config.debug) {
    }

    this.reconnectTimer = setTimeout(() => {
      this.emit(WebSocketEventType.RECONNECT, {
        attempt: this.reconnectAttempts,
      });
      this.connect();
    }, delay);
  }

  /**
   * 清除重连定时器
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * 清除连接超时定时器
   */
  private clearConnectionTimer(): void {
    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
      this.connectionTimer = null;
    }
  }

  /**
   * 处理消息队列
   */
  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) {
      return;
    }

    if (this.config.debug) {
    }

    const queue = [...this.messageQueue];
    this.messageQueue = [];

    queue.forEach(({ message, options }) => {
      this.send(message, { ...options, queue: false });
    });
  }

  /**
   * 设置连接状态
   */
  private setState(state: ConnectionState): void {
    if (this.state === state) {
      return;
    }

    const oldState = this.state;
    this.state = state;

    if (this.config.debug) {
    }

    this.emit(WebSocketEventType.STATE_CHANGE, {
      oldState,
      newState: state,
    });
  }

  /**
   * 销毁客户端
   */
  destroy(): void {
    if (this.config.debug) {
    }

    this.disconnect();
    this.unsubscribeAll();
    this.requestResponseManager.clear();
    this.eventListeners.clear();
    this.messageQueue = [];
  }
}
